import { Select } from "./Select";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import {
  displayValue,
  qrAskedParts,
  qrPayload,
  qrPartValue,
  qrParts,
  qrTypes,
  type Field,
  type Values,
} from "@proof/core";
import { AssetImage } from "./AssetImage";
import type { FieldInputProps } from "./FormGenerator";

function Preview({ field, values }: { field: Field; values: Values }) {
  const payload = qrPayload(field, values);
  const matrix = useMemo(() => {
    try {
      return payload && payload.length <= 2000
        ? QRCode.create(payload, { errorCorrectionLevel: "M" })
        : undefined;
    } catch {
      return undefined;
    }
  }, [payload]);
  if (!matrix)
    return (
      <p className="field-help">
        {payload.length > 2000
          ? "QR code details exceed 2,000 characters."
          : "Enter QR code details to see a preview."}
      </p>
    );
  const size = matrix.modules.size + 2,
    paths: string[] = [];
  for (let y = 0; y < matrix.modules.size; y++)
    for (let x = 0; x < matrix.modules.size; x++)
      if (matrix.modules.get(y, x)) paths.push(`M${x + 1},${y + 1}h1v1h-1z`);
  return (
    <svg
      role="img"
      aria-label={`${field.label} QR code preview`}
      width="160"
      height="160"
      viewBox={`0 0 ${size} ${size}`}
      style={{ maxWidth: "100%", height: "auto", display: "block" }}
      shapeRendering="crispEdges"
    >
      <rect
        width={size}
        height={size}
        fill={
          field.qr?.background === "transparent"
            ? "white"
            : field.qr?.background || "white"
        }
      />
      <path d={paths.join("")} fill={field.qr?.foreground || "#183c3c"} />
    </svg>
  );
}

function Editor({
  field,
  values,
  initial,
  readOnly,
  onClose,
  onSave,
}: {
  field: Field;
  values: Values;
  initial: Record<string, unknown>;
  readOnly: boolean;
  onClose(): void;
  onSave(value: Values[string]): void;
}) {
  const dialog = useRef<HTMLDialogElement>(null),
    title = useId();
  const [draft, setDraft] = useState(initial),
    [error, setError] = useState("");
  const parts = qrParts(field);
  const asked = new Set(qrAskedParts(field).map((part) => part.key));
  const preview = { ...values, [field.id]: draft } as Values;
  useEffect(() => {
    const element = dialog.current!;
    element.showModal();
    return () => {
      if (element.open) element.close();
    };
  }, []);
  const dismiss = () => {
    dialog.current?.close();
    onClose();
  };
  return (
    <dialog
      ref={dialog}
      className="proof-qr-editor"
      aria-labelledby={title}
      onCancel={(event) => {
        event.preventDefault();
        event.stopPropagation();
        dismiss();
      }}
      onClose={(event) => {
        event.stopPropagation();
        if (!event.currentTarget.open) onClose();
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape" || event.key === "Tab")
          event.stopPropagation();
        if (event.key === "Enter" && event.target instanceof HTMLInputElement) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
    >
      <style>{styles}</style>
      <header>
        <div>
          <h2 id={title}>
            {readOnly ? "View QR code" : "Edit QR code"} · {field.label}
          </h2>
          <p>{qrTypes[field.qr?.kind || "website"].label}</p>
        </div>
        <button
          type="button"
          aria-label="Close QR code editor"
          onClick={dismiss}
        >
          ×
        </button>
      </header>
      <div className="qr-editor-layout">
        <div className="qr-editor-parts">
          {parts.map((part) => {
            const mapped = field.qr?.mapping[part.key],
              fixed = field.qr?.fixed?.[part.key];
            const value = qrPartValue(field, values, part.key, draft);
            const disabled = readOnly || !asked.has(part.key);
            const props = {
              disabled,
              "aria-label": part.label,
              value: displayValue(value),
              onChange: (
                event: React.ChangeEvent<
                  HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
                >,
              ) => setDraft({ ...draft, [part.key]: event.target.value }),
            };
            return (
              <label key={part.key}>
                <span>{part.label}</span>
                {part.input === "checkbox" ? (
                  <input
                    type="checkbox"
                    aria-label={part.label}
                    disabled={disabled}
                    checked={value === true || value === "true"}
                    onChange={(event) =>
                      setDraft({ ...draft, [part.key]: event.target.checked })
                    }
                  />
                ) : part.input === "textarea" ? (
                  <textarea {...props} rows={3} />
                ) : part.input === "select" ? (
                  <Select
                    {...props}
                    value={
                      displayValue(value) || part.options?.[0]?.value || ""
                    }
                  >
                    {part.options?.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <input
                    {...props}
                    type={part.input || "text"}
                    step={part.input === "number" ? "any" : undefined}
                    min={
                      part.key === "latitude"
                        ? -90
                        : part.key === "longitude"
                          ? -180
                          : undefined
                    }
                    max={
                      part.key === "latitude"
                        ? 90
                        : part.key === "longitude"
                          ? 180
                          : undefined
                    }
                    placeholder={part.placeholder}
                  />
                )}
                {mapped && <small>Linked to another field.</small>}
                {!mapped && field.qr?.defaults?.[part.key] && (
                  <small>
                    Default from another field; you can change it here.
                  </small>
                )}
                {fixed !== undefined && (
                  <small>Provided by the document.</small>
                )}
              </label>
            );
          })}
        </div>
        <aside>
          <Preview field={field} values={preview} />
        </aside>
      </div>
      {error && (
        <p role="alert" className="error-text">
          {error}
        </p>
      )}
      <footer>
        <button type="button" onClick={dismiss}>
          {readOnly ? "Close" : "Cancel"}
        </button>
        {!readOnly && (
          <button
            type="button"
            onClick={() => {
              for (const control of dialog.current!.querySelectorAll<
                HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
              >("input,select,textarea"))
                if (!control.reportValidity()) return;
              const payload = qrPayload(field, preview);
              try {
                if (
                  field.qr?.kind === "location" &&
                  qrParts(field).some((part) => part.key === "latitude") &&
                  !payload &&
                  ["latitude", "longitude"].some((key) =>
                    displayValue(qrPartValue(field, values, key, draft)).trim(),
                  )
                )
                  throw Error(
                    "Enter both latitude (-90 to 90) and longitude (-180 to 180).",
                  );
                if (payload.length > 2000)
                  throw Error("QR code details exceed 2,000 characters.");
                if (payload)
                  QRCode.create(payload, { errorCorrectionLevel: "M" });
                onSave(draft as Values[string]);
              } catch (error) {
                setError(
                  error instanceof Error
                    ? error.message
                    : "These details cannot be encoded as a QR code.",
                );
              }
            }}
          >
            Apply QR code
          </button>
        )}
      </footer>
    </dialog>
  );
}

export function QrField({
  field,
  value,
  values,
  onChange,
  upload,
  assetUrl,
  loadAsset,
  scope,
  disabled,
}: {
  field: Field;
  value: Values[string];
  values: Values;
  scope: string;
  disabled: boolean;
} & Pick<FieldInputProps, "onChange" | "upload" | "assetUrl" | "loadAsset">) {
  const asked = qrAskedParts(field);
  const answers: Record<string, unknown> =
    value && typeof value === "object" && !Array.isArray(value)
      ? value
      : typeof value === "string" && /^asset_[a-f0-9]{64}$/.test(value)
        ? { assetId: value }
        : typeof value === "string" && asked[0]
          ? { [asked[0].key]: value }
          : {};
  const assetId =
    typeof answers.assetId === "string" &&
    /^asset_[a-zA-Z0-9_-]+$/.test(answers.assetId)
      ? answers.assetId
      : undefined;
  const { assetId: _asset, adjustments: _adjustments, ...details } = answers;
  const [editing, setEditing] = useState<string>(),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const live = useRef(scope),
    pending = useRef<AbortController | undefined>(undefined);
  live.current = scope;
  useEffect(() => {
    setEditing(undefined);
    setError("");
    setBusy(false);
    return () => {
      pending.current?.abort();
      pending.current = undefined;
    };
  }, [scope, disabled, upload]);
  const uploadFile = async (file: File) => {
    if (disabled || !upload || !field.image.allowUpload || pending.current)
      return;
    setError("");
    if (file.size > field.image.maxBytes) {
      setError("QR image exceeds the configured file size limit.");
      return;
    }
    if (!/\.(png|jpe?g|webp|tiff?)$/i.test(file.name)) {
      setError("Choose a PNG, JPEG, WebP or TIFF QR image.");
      return;
    }
    const controller = new AbortController(),
      started = scope;
    pending.current = controller;
    setBusy(true);
    try {
      const image = await upload(file, field, controller.signal);
      if (!controller.signal.aborted && live.current === started)
        onChange({ ...details, assetId: image.id } as Values[string]);
    } catch (error) {
      if (!controller.signal.aborted && live.current === started)
        setError(
          error instanceof Error ? error.message : "QR image upload failed.",
        );
    } finally {
      if (pending.current === controller) {
        pending.current = undefined;
        setBusy(false);
      }
    }
  };
  return (
    <div
      className="qr-field image-field"
      role="group"
      aria-label={field.label}
      data-qr-kind={field.qr?.kind || "website"}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        if (event.dataTransfer.files[0])
          void uploadFile(event.dataTransfer.files[0]);
      }}
    >
      {assetId ? (
        <AssetImage
          assetId={assetId}
          assetUrl={assetUrl}
          loadAsset={loadAsset}
          className="asset-thumbnail"
          alt={`${field.label} uploaded QR code`}
        />
      ) : (
        <Preview field={field} values={{ ...values, [field.id]: value }} />
      )}
      {!disabled && upload && field.image.allowUpload && (
        <label className="upload-zone">
          <input
            type="file"
            aria-label={`Upload ${field.label} QR code image`}
            accept="image/png,image/jpeg,image/webp,image/tiff"
            disabled={busy}
            onChange={(event) => {
              if (event.target.files?.[0])
                void uploadFile(event.target.files[0]);
              event.target.value = "";
            }}
          />
          <span>
            {busy
              ? "Uploading QR image…"
              : assetId
                ? "Replace QR code image"
                : "Drop a QR code image or browse"}
          </span>
          <small>
            PNG, JPEG, WebP, TIFF · up to{" "}
            {Math.round(field.image.maxBytes / 1000000)} MB
          </small>
        </label>
      )}
      <div className="image-field-actions">
        <button
          type="button"
          aria-haspopup="dialog"
          disabled={busy}
          onClick={() => setEditing(scope)}
        >
          {disabled ? "View QR code" : "Edit QR code"}
        </button>
        {!disabled && assetId && (
          <button
            type="button"
            onClick={() => onChange(details as Values[string])}
          >
            Use generated QR code
          </button>
        )}
        {busy && (
          <button
            type="button"
            onClick={() => {
              pending.current?.abort();
              pending.current = undefined;
              setBusy(false);
            }}
          >
            Cancel upload
          </button>
        )}
      </div>
      {assetId && (
        <p className="field-help">
          Uploaded QR image. Editing details creates a replacement code; the
          image's encoded contents are not decoded.
        </p>
      )}
      {error && (
        <p role="alert" className="error-text">
          {error}
        </p>
      )}
      {editing === scope && (
        <Editor
          field={field}
          values={values}
          initial={details}
          readOnly={disabled}
          onClose={() => setEditing(undefined)}
          onSave={(value) => {
            if (disabled || live.current !== scope) return;
            setEditing(undefined);
            onChange(value);
          }}
        />
      )}
    </div>
  );
}

const styles = `.proof-qr-editor{box-sizing:border-box;width:min(800px,calc(100vw - 32px));max-height:calc(100dvh - 32px);overflow:auto;border:1px solid var(--color-border,#c6cfca);border-radius:12px;padding:24px;background:var(--color-surface,#fff);color:var(--color-text,#22312b)}.proof-qr-editor::backdrop{background:rgb(24 33 29 / .55)}.proof-qr-editor header,.proof-qr-editor footer{display:flex;justify-content:space-between;gap:16px;align-items:center}.proof-qr-editor header h2{margin:0}.proof-qr-editor .qr-editor-layout{display:grid;grid-template-columns:minmax(0,1fr) 180px;gap:24px;margin:24px 0}.proof-qr-editor .qr-editor-parts{display:grid;gap:16px}.proof-qr-editor label{display:block}.proof-qr-editor label>span,.proof-qr-editor small{display:block}.proof-qr-editor input:not([type=checkbox]),.proof-qr-editor textarea,.proof-qr-editor select{box-sizing:border-box;width:100%;margin-top:6px;padding:10px;border:1px solid var(--color-border,#c6cfca);border-radius:6px;background:var(--color-surface,#fff);color:inherit}.proof-qr-editor footer{justify-content:flex-end}@media(max-width:600px){.proof-qr-editor .qr-editor-layout{grid-template-columns:minmax(0,1fr)}.proof-qr-editor aside{grid-row:1}}`;
